import { DateTime, Duration, Interval } from 'luxon';

import { RejectionsCategoriesLabels } from 'apis/merchant-gateway/insights';

import { DateRange, GrowthTrend, JsonPath, Period } from './types';

export const calculateGrowthRate = (before: number, after: number) => {
  if (before === 0) {
    return null;
  }
  const difference = after - before;
  const growthRate = difference / before;
  return growthRate;
};

export const getGrowthRateForFields = <V extends { [key in string]: number }, T extends keyof V>(
  totalForSelectedDateRange: V,
  totalForPreviousDateRange: V,
  keys: T[],
) => {
  const currValues = keys.map(key => totalForSelectedDateRange[key]);
  const prevValues = keys.map(key => totalForPreviousDateRange[key]);
  return Object.fromEntries(
    currValues.map((currValue, index) => [keys[index], calculateGrowthRate(prevValues[index], currValue)]),
  ) as { [key in T]: number };
};

export const getGrowthTrend = (value: number | null | undefined, invert: boolean = false): GrowthTrend => {
  value = value ?? 0;
  value = invert ? -value : value;
  return value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral';
};

export const formatGrowthRate = (growthRate: number | null | undefined, locale: string) =>
  growthRate?.toLocaleString(locale, {
    style: 'percent',
    signDisplay: 'exceptZero',
    maximumFractionDigits: Math.abs(growthRate) >= 1 / 100 ? 0 : 1,
  }) ?? '';

export const formatPercentage = (num: number | null, locale: string) =>
  num?.toLocaleString(locale, {
    style: 'percent',
    signDisplay: 'never',
    maximumFractionDigits: 2,
  }) ?? '';

export const getOldestAvailableDay = () => DateTime.now().minus({ year: 2 }).startOf('day');

export const getPrevDateInterval = (interval: Interval, oldestAvailableDay = getOldestAvailableDay()) => {
  const daysDuration = interval.toDuration('days'); // 'days' is important here, so that luxon will take into account DST changes
  const prevEndDate = interval.start.minus({ day: 1 }).startOf('day');
  const prevStartDate = prevEndDate.minus(daysDuration).startOf('day');

  // if comparison period is out of range supported by the API then we don't want to query it
  if (prevStartDate.diff(oldestAvailableDay).milliseconds < 0) {
    return null;
  }

  return Interval.fromDateTimes(prevStartDate, prevEndDate);
};

export const formatDuration = (duration: Duration) => {
  const hours = Math.floor(duration.as('hours'));
  const remainingMinutes = Math.floor(duration.as('minutes') % 60);

  const hourStr = hours ? `${hours}h` : '';
  const minStr = remainingMinutes ? `${remainingMinutes}m` : '';
  return `${hourStr} ${minStr}`.trim() || '0m';
};

export const formatSeconds = (seconds: number) => {
  return formatDuration(Duration.fromObject({ seconds }));
};

export const formatMinutes = (minutes: number) => {
  return formatDuration(Duration.fromObject({ minutes }));
};

const RejectionCategoryMapping: Record<RejectionsCategoriesLabels, JsonPath> = {
  [RejectionsCategoriesLabels.MINUTE_REJECTION_15]: 'insights.rejections.reasons.15_minute_rejection',
  [RejectionsCategoriesLabels.MINUTE_REJECTION_3]: 'insights.rejections.reasons.3_minute_rejection',
  [RejectionsCategoriesLabels.ACCIDENTAL_PREORDER]: 'insights.rejections.reasons.accidental_preorder',
  [RejectionsCategoriesLabels.ACCIDENTAL_TAKEAWAY]: 'insights.rejections.reasons.accidental_takeaway',
  [RejectionsCategoriesLabels.CLOSING_SOON]: 'insights.rejections.reasons.closing_soon',
  [RejectionsCategoriesLabels.CUSTOMER_CANCEL_ON_ORDER_PREVIEW]:
    'insights.rejections.reasons.customer_cancel_on_order_preview',
  [RejectionsCategoriesLabels.CUSTOMER_CANCEL_ON_ORDER_REVIEW]:
    'insights.rejections.reasons.customer_cancel_on_order_review',
  [RejectionsCategoriesLabels.CUSTOMER_NOT_HAPPY_WITH_ETA]: 'insights.rejections.reasons.customer_not_happy_with_eta',
  [RejectionsCategoriesLabels.GENERIC_CUSTOMER_REQUESTED]: 'insights.rejections.reasons.generic_customer_requested',
  [RejectionsCategoriesLabels.GENERIC_REJECTION]: 'insights.rejections.reasons.generic_rejection',
  [RejectionsCategoriesLabels.GENERIC_VENUE_REQUESTED]: 'insights.rejections.reasons.generic_venue_requested',
  [RejectionsCategoriesLabels.ITEM_UNAVAILABLE]: 'insights.rejections.reasons.item_unavailable',
  [RejectionsCategoriesLabels.NO_COURIERS_AVAILABLE]: 'insights.rejections.reasons.no_couriers_available',
  [RejectionsCategoriesLabels.ORDER_CANNOT_BE_DELIVERED]: 'insights.rejections.reasons.order_cannot_be_delivered',
  [RejectionsCategoriesLabels.CUSTOMER]: 'insights.rejections.reasons.generic_customer_requested',
  [RejectionsCategoriesLabels.VENUE_TOO_BUSY]: 'insights.rejections.reasons.venue_too_busy',
};

export const extractRejectionCategory = ({
  label,
}: {
  label: RejectionsCategoriesLabels | null | undefined;
}): JsonPath => {
  return label && RejectionCategoryMapping[label] ? RejectionCategoryMapping[label] : 'insights.unknown';
};

/**
 * Calculates the average value of two values
 * @param previousAvg - the previous average value
 * @param previousCount - the previous count of values
 * @param nextAvg - the next average value
 * @param nextCount - the next count of values
 * @returns the average value of the two values
 * @example calculateAvgValue({ previousAvg: 10, previousCount: 2, nextAvg: 20, nextCount: 3 }) => 16
 */
export const calculateAvgValue = ({
  previousAvg,
  previousCount,
  nextAvg,
  nextCount,
}: {
  previousAvg: number;
  previousCount: number;
  nextAvg: number;
  nextCount: number;
}) => {
  const previousSum = previousAvg * previousCount;
  const nextSum = nextAvg * nextCount;

  const resultSum = previousSum + nextSum;
  const resultCount = nextCount + previousCount;

  if (resultCount === 0) {
    return 0;
  }

  const resultAvg = resultSum / resultCount;
  return resultAvg;
};

export const isAllError = (allInsightResponses: { isError?: boolean; isLoading?: boolean; isFetching?: boolean }[]) =>
  // if a query is isLoading but not isFetching, it means that it is disabled with `enabled: false` or we're offline
  // let's show error for such state instead of infinite loading
  allInsightResponses.every(insight => insight.isError || (insight.isLoading && !insight.isFetching));

export const isInitialLoading = (
  allInsightResponses: { isError?: boolean; isLoading?: boolean; isFetching?: boolean }[],
) =>
  // show page loader if some data is still in an ititial fetching state (indicated by isFetching + isLoading)
  allInsightResponses.some(insight => insight.isFetching && insight.isLoading) ||
  // if were refetching ALL data after an error, show page loader
  allInsightResponses.every(insight => insight.isFetching && insight.isError);

export const getDateRanges = (): {
  [key in Period]: DateRange;
} & {
  disabledDays: {
    before: Date;
    after: Date;
  };
} => {
  // TODO @paipo: add unit tests for the below scenarios after Maria finalized the requirments
  const today = DateTime.now();
  const yesterday = today.minus({ days: 1 });
  const firstDayOfLastWeek = today.minus({ week: 1 }).startOf('week');
  const sevenDaysAgo = today.minus({ days: 6 });
  const lastDayOfLastWeek = today.minus({ week: 1 }).endOf('week');
  const firstDayOfThisMonth = today.startOf('month');
  const dayOf28DaysBefore = today.minus({ days: 27 });

  const todayDateRange = {
    from: today.toJSDate(),
    to: today.toJSDate(),
    name: Period.YESTERDAY,
  } as const;

  const yesterdayDateRange = {
    from: yesterday.toJSDate(),
    to: yesterday.toJSDate(),
    name: Period.YESTERDAY,
  } as const;
  const lastWeekDateRange = {
    from: firstDayOfLastWeek.toJSDate(),
    to: lastDayOfLastWeek.toJSDate(),
    name: Period.LAST_WEEK,
  } as const;
  const thisMonthDateRange = {
    from: firstDayOfThisMonth.toJSDate(),
    // to cannot be earlier than from if today is the first day of the month
    to: DateTime.max(firstDayOfThisMonth, today).toJSDate(),
    name: Period.THIS_MONTH,
  } as const;
  const last28daysDateRange = {
    from: dayOf28DaysBefore.toJSDate(),
    to: today.toJSDate(),
    name: Period.LAST_28_DAYS,
  } as const;
  const sevenDaysAgoDateRange = {
    from: sevenDaysAgo.toJSDate(),
    to: today.toJSDate(),
    name: Period.SEVEN_DAYS_AGO,
  } as const;

  const disabledDays = {
    before: getOldestAvailableDay().toJSDate(),
    after: today.toJSDate(),
  };

  return {
    todayDateRange,
    yesterdayDateRange,
    lastWeekDateRange,
    thisMonthDateRange,
    last28daysDateRange,
    sevenDaysAgoDateRange,
    disabledDays,
  };
};
